import { DEFAULT_ORG_THEME } from '../constants/brandColors';
import { orgLogoUrl } from './orgLogo';

const HEX = /^#[0-9A-F]{6}$/i;

const valid = (value) =>
  typeof value === 'string' && HEX.test(value.trim()) ? value.trim().toUpperCase() : null;

const SYSTEM_SANS = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

// Typefaces offered in Super Admin > Branding. Source Serif is serif headings
// with a sans body, as its card says. Fonts are loaded in public/index.html.
export const BRAND_TYPEFACES = {
  Sora: { body: "'Sora', sans-serif", heading: "'Sora', sans-serif" },
  'IBM Plex Sans': { body: "'IBM Plex Sans', sans-serif", heading: "'IBM Plex Sans', sans-serif" },
  'Source Serif': { body: SYSTEM_SANS, heading: "'Source Serif 4', 'Source Serif Pro', Georgia, serif" },
  'System sans': { body: SYSTEM_SANS, heading: SYSTEM_SANS },
};

// typeface = 'Custom' selects the organization's uploaded font file
// (organizations.font_path), registered under one fixed family name.
export const CUSTOM_TYPEFACE = 'Custom';
export const CUSTOM_FONT_FAMILY = 'Ethos Brand Upload';
export const CUSTOM_FONT_STACK = `'${CUSTOM_FONT_FAMILY}', ${SYSTEM_SANS}`;

let customFace = null; // { url, face, ready }

// Registers the uploaded font with the browser (once per URL) and returns a
// promise that resolves to true when it has loaded. A new upload replaces the
// previous face, so only one uploaded font is ever active.
export const loadCustomFont = (url) => {
  if (!url || typeof window === 'undefined' || typeof window.FontFace === 'undefined') {
    return Promise.resolve(false);
  }
  if (customFace?.url === url) return customFace.ready;
  if (customFace) document.fonts.delete(customFace.face);

  const face = new window.FontFace(CUSTOM_FONT_FAMILY, `url("${url}")`, { display: 'swap' });
  document.fonts.add(face);
  const ready = face.load().then(
    () => true,
    (error) => {
      console.warn('Could not load the uploaded brand font:', error);
      return false;
    }
  );
  customFace = { url, face, ready };
  return ready;
};

// Sets --org-font-body / --org-font-heading for a typeface name; unknown or
// empty names clear them so screens keep their own font. Screens opt in with
// the .brand-font class (see index.css). customFontUrl is the uploaded font
// file, used when typeface is 'Custom'.
export const applyBrandFont = (typeface, customFontUrl) => {
  const root = document.documentElement;
  if (typeface === CUSTOM_TYPEFACE && customFontUrl) {
    loadCustomFont(customFontUrl);
    root.style.setProperty('--org-font-body', CUSTOM_FONT_STACK);
    root.style.setProperty('--org-font-heading', CUSTOM_FONT_STACK);
    return;
  }
  const font = BRAND_TYPEFACES[typeface];
  if (font) {
    root.style.setProperty('--org-font-body', font.body);
    root.style.setProperty('--org-font-heading', font.heading);
  } else {
    root.style.removeProperty('--org-font-body');
    root.style.removeProperty('--org-font-heading');
  }
};

// The one place an organization's branding is applied: its four colours (unset
// ones fall back to DEFAULT_ORG_THEME) and its typeface. Every shell (Super
// Admin, Org Admin, Manager, Learner) calls this with its own organization;
// pass {} for the platform defaults.
// Accepts the Organizations API shape: primary_color, accent_color, chrome_color, support_color,
// typeface, font_path.
export const applyBrandVariables = (org = {}) => {
  const source = org || {};
  applyBrandFont(source.typeface, orgLogoUrl(source.font_path));
  const root = document.documentElement;
  const colours = {
    '--org-primary': valid(source.primary_color),
    '--org-accent': valid(source.accent_color),
    '--org-chrome': valid(source.chrome_color),
    '--org-support': valid(source.support_color),
  };

  Object.entries(DEFAULT_ORG_THEME).forEach(([name, fallback]) => {
    root.style.setProperty(name, colours[name] || fallback);
  });

  // brand-shell.css still reads the older --brand-* names.
  root.style.setProperty('--brand-primary', colours['--org-primary'] || DEFAULT_ORG_THEME['--org-primary']);
  root.style.setProperty('--brand-accent', colours['--org-accent'] || DEFAULT_ORG_THEME['--org-accent']);
};